/**
 * Unified Cache Service
 *
 * Consolidates all caching logic with:
 * - LRU (Least Recently Used) eviction
 * - Memory size limits
 * - Performance monitoring
 * - Automatic cleanup
 * - Storage persistence option
 *
 * Copyright Deskshare Inc.
 * All rights reserved.
 */

import { logger } from "@/lib/logger";

interface CacheEntry<T> {
  data: T;
  createdAt: number;
  lastAccessedAt: number;
  expiresAt: number;
  size: number; // Approximate size in bytes
}

interface CacheOptions {
  /** Default TTL in milliseconds */
  defaultTTL?: number;
  /** Maximum number of entries */
  maxEntries?: number;
  /** Maximum size in bytes (approximate) */
  maxSizeBytes?: number;
  /** Enable persistence to localStorage */
  enablePersistence?: boolean;
  /** Storage key prefix */
  storagePrefix?: string;
  /** Cleanup interval in milliseconds */
  cleanupInterval?: number;
}

interface CacheStats {
  hits: number;
  misses: number;
  evictions: number;
  entries: number;
  sizeBytes: number;
}

const DEFAULT_OPTIONS: Required<CacheOptions> = {
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxEntries: 500,
  maxSizeBytes: 10 * 1024 * 1024, // 10 MB
  enablePersistence: false,
  storagePrefix: "cache_",
  cleanupInterval: 60 * 1000, // 1 minute
};

/**
 * Cache TTL presets (in milliseconds)
 */
export const CACHE_TTL = {
  /** 30 seconds - very fast changing data */
  INSTANT: 30 * 1000,
  /** 1 minute - fast changing data */
  SHORT: 60 * 1000,
  /** 5 minutes - moderate change rate */
  MEDIUM: 5 * 60 * 1000,
  /** 30 minutes - slow changing data */
  LONG: 30 * 60 * 1000,
  /** 1 hour - very stable data */
  STABLE: 60 * 60 * 1000,
  /** 24 hours - almost never changes */
  PERMANENT: 24 * 60 * 60 * 1000,
} as const;

/**
 * Unified cache service with LRU eviction and performance tracking
 */
class CacheService {
  private cache = new Map<string, CacheEntry<unknown>>();
  private options: Required<CacheOptions>;
  private stats: CacheStats = {
    hits: 0,
    misses: 0,
    evictions: 0,
    entries: 0,
    sizeBytes: 0,
  };
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: CacheOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.startCleanupTimer();

    // Load persisted data if enabled
    if (this.options.enablePersistence) {
      this.loadFromStorage();
    }
  }

  /**
   * Get value from cache
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check expiration
    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      this.stats.misses++;
      return null;
    }

    // Update last accessed time for LRU
    entry.lastAccessedAt = Date.now();
    this.stats.hits++;

    return entry.data;
  }

  /**
   * Set value in cache
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const expiresAt = now + (ttl ?? this.options.defaultTTL);
    const size = this.estimateSize(data);

    // Check if we need to evict entries
    this.ensureCapacity(size);

    const entry: CacheEntry<T> = {
      data,
      createdAt: now,
      lastAccessedAt: now,
      expiresAt,
      size,
    };

    // Update stats for existing entry
    const existing = this.cache.get(key);
    if (existing) {
      this.stats.sizeBytes -= existing.size;
    }

    this.cache.set(key, entry);
    this.stats.entries = this.cache.size;
    this.stats.sizeBytes += size;

    // Persist if enabled
    if (this.options.enablePersistence) {
      this.persistEntry(key, entry);
    }
  }

  /**
   * Delete entry from cache
   */
  delete(key: string): boolean {
    const entry = this.cache.get(key);
    if (entry) {
      this.stats.sizeBytes -= entry.size;
      this.cache.delete(key);
      this.stats.entries = this.cache.size;

      if (this.options.enablePersistence) {
        this.removeFromStorage(key);
      }
      return true;
    }
    return false;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      return false;
    }
    return true;
  }

  /**
   * Delete all entries matching a prefix
   */
  deleteByPrefix(prefix: string): number {
    let deleted = 0;
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.delete(key);
        deleted++;
      }
    }
    return deleted;
  }

  /**
   * Clear all entries
   */
  clear(): void {
    this.cache.clear();
    this.stats.entries = 0;
    this.stats.sizeBytes = 0;

    if (this.options.enablePersistence) {
      this.clearStorage();
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): Readonly<CacheStats> {
    return { ...this.stats };
  }

  /**
   * Get hit rate percentage
   */
  getHitRate(): number {
    const total = this.stats.hits + this.stats.misses;
    if (total === 0) return 0;
    return (this.stats.hits / total) * 100;
  }

  /**
   * Get or set - fetches and caches if not found
   */
  async getOrSet<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl?: number,
  ): Promise<T> {
    // Check cache first
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Fetch and cache
    const data = await fetcher();
    if (data !== null && data !== undefined) {
      this.set(key, data, ttl);
    }
    return data;
  }

  /**
   * Warm up cache with multiple entries
   */
  warmUp<T>(entries: Array<{ key: string; data: T; ttl?: number }>): void {
    for (const { key, data, ttl } of entries) {
      this.set(key, data, ttl);
    }
    logger.debug(`Cache warmed up with ${entries.length} entries`);
  }

  /**
   * Cleanup and shutdown
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.clear();
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Estimate size of data in bytes
   */
  private estimateSize(data: unknown): number {
    try {
      const json = JSON.stringify(data);
      // UTF-16 encoding: approximately 2 bytes per character
      return json.length * 2;
    } catch {
      // Fallback for circular references or non-serializable data
      return 1024; // Assume 1KB
    }
  }

  /**
   * Ensure cache has capacity for new entry
   */
  private ensureCapacity(newSize: number): void {
    // Check entry count limit
    while (this.cache.size >= this.options.maxEntries) {
      this.evictLRU();
    }

    // Check size limit
    while (this.stats.sizeBytes + newSize > this.options.maxSizeBytes) {
      if (!this.evictLRU()) break; // No more entries to evict
    }
  }

  /**
   * Evict least recently used entry
   */
  private evictLRU(): boolean {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessedAt < oldestTime) {
        oldestTime = entry.lastAccessedAt;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.delete(oldestKey);
      this.stats.evictions++;
      return true;
    }
    return false;
  }

  /**
   * Start automatic cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanupExpired();
    }, this.options.cleanupInterval);
  }

  /**
   * Remove all expired entries
   */
  private cleanupExpired(): void {
    const now = Date.now();
    let removed = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.delete(key);
        removed++;
      }
    }

    if (removed > 0) {
      logger.debug(`Cache cleanup: removed ${removed} expired entries`);
    }
  }

  /**
   * Persist entry to localStorage
   */
  private persistEntry<T>(key: string, entry: CacheEntry<T>): void {
    try {
      const storageKey = this.options.storagePrefix + key;
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      // localStorage might be full or disabled
      logger.debug("Failed to persist cache entry:", error);
    }
  }

  /**
   * Remove entry from localStorage
   */
  private removeFromStorage(key: string): void {
    try {
      const storageKey = this.options.storagePrefix + key;
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Clear all persisted entries
   */
  private clearStorage(): void {
    try {
      const prefix = this.options.storagePrefix;
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Load persisted entries from localStorage
   */
  private loadFromStorage(): void {
    try {
      const prefix = this.options.storagePrefix;
      const keys = Object.keys(localStorage);
      let loaded = 0;

      for (const storageKey of keys) {
        if (!storageKey.startsWith(prefix)) continue;

        const key = storageKey.slice(prefix.length);
        const raw = localStorage.getItem(storageKey);
        if (!raw) continue;

        try {
          const entry = JSON.parse(raw) as CacheEntry<unknown>;

          // Skip expired entries
          if (Date.now() > entry.expiresAt) {
            localStorage.removeItem(storageKey);
            continue;
          }

          this.cache.set(key, entry);
          this.stats.sizeBytes += entry.size;
          loaded++;
        } catch {
          // Remove corrupted entry
          localStorage.removeItem(storageKey);
        }
      }

      this.stats.entries = this.cache.size;

      if (loaded > 0) {
        logger.debug(`Loaded ${loaded} entries from persistent storage`);
      }
    } catch {
      // Storage might be disabled
    }
  }
}

// ============================================================================
// Singleton Instances
// ============================================================================

/**
 * Default in-memory cache for API responses
 */
export const apiCache = new CacheService({
  defaultTTL: CACHE_TTL.MEDIUM,
  maxEntries: 200,
  maxSizeBytes: 5 * 1024 * 1024, // 5 MB
  enablePersistence: false,
});

/**
 * Persistent cache for user preferences and stable data
 */
export const persistentCache = new CacheService({
  defaultTTL: CACHE_TTL.PERMANENT,
  maxEntries: 100,
  maxSizeBytes: 2 * 1024 * 1024, // 2 MB
  enablePersistence: true,
  storagePrefix: "shop_cache_",
});

// ============================================================================
// Cache Key Builders
// ============================================================================

/**
 * Build consistent cache keys
 */
export const cacheKeys = {
  /**
   * N-16 (S697): ONE signature for the licence cache key, taking the payment
   * intent and the OPTIONAL product identifier.
   *
   * The key used to be composed by each caller. `fetchLicense` built
   * ``cacheKeys.license(`${payment_intent}:${productId}`)`` when it had a
   * product and ``cacheKeys.license(payment_intent)`` when it did not, while
   * the invalidator in product-table.tsx and `fetchFreshLicense` only ever
   * built the bare form. Both CATALOGUE writers supply a product, so every
   * entry the catalogue held carried the suffix and every deletion removed a
   * key the catalogue had not written -- the stale entry survived its full
   * sixty seconds and the licence dialog showed the old activation count.
   *
   * The bare form is NOT unused, and an earlier draft of this comment said it
   * was. `fetchLicenseWithRetry` composes it and stores the licences under it
   * for THIRTY minutes (CACHE_TTL.LONG), and that is the fetcher the thank-you
   * page's invoice table runs. So both keys have a writer, both invalidators
   * delete both, and neither deletion is decoration. Pinned in
   * lib/__tests__/s697-license-cache.test.ts.
   *
   * The string this produces is byte-for-byte what the old two-branch
   * composition produced, so no stored entry changes shape; what changes is
   * that there is now exactly one place the composition happens.
   */
  license: (paymentIntent: string, productId?: string) =>
    productId
      ? `license:${paymentIntent}:${productId}`
      : `license:${paymentIntent}`,
  licenses: () => "licenses:all",
  product: (productId: string) => `product:${productId}`,
  productName: (productId: string) => `product_name:${productId}`,
  paymentMethod: (intentId: string) => `payment_method:${intentId}`,
  profile: (userId?: string) => `profile:${userId || "current"}`,
  orders: (userId?: string) => `orders:${userId || "current"}`,
  subscriptions: (userId?: string) => `subscriptions:${userId || "current"}`,
};

/**
 * Invalidate all user-related cache entries
 */
/**
 * A-6 (FCR S771): the ONE way a licence cache entry is dropped.
 *
 * Every licence entry has two possible keys -- the catalogue's, which carries
 * the product (`fetchLicense`, sixty seconds), and the bare one
 * `fetchLicenseWithRetry` writes for the thank-you page (thirty minutes) -- and
 * both have a writer, so an invalidation that drops one leaves a stale
 * activation count on screen (N-16, S697). The two places that invalidate
 * (the device list after a deactivation, and the thank-you page when a licence
 * arrives) used to compose both deletions themselves. They call this instead,
 * so a third key shape, if one is ever added, is added here once.
 */
export function invalidateLicenseCache(
  paymentIntent: string,
  productIds: Iterable<string> = [],
): void {
  if (!paymentIntent) return;
  apiCache.delete(cacheKeys.license(paymentIntent));
  for (const productId of productIds) {
    if (productId) apiCache.delete(cacheKeys.license(paymentIntent, productId));
  }
}

export { CacheService };
export type { CacheOptions, CacheStats };
